/*
 * Liefertermin aus der Shipping-App ("Lieferung Do, 08.10. – Fr, 09.10. · nach 84028"),
 * gemeinsam für den Preisblock (altruan-pdp-versand.js) und die Produktkarten (altruan-card-lieferung.js).
 *
 * Die App rechnet das Datum nur aus Land + Bestand + Lieferort (variant_id und Menge wertet sie für
 * Ware auf Lager nicht aus), daher eine Anfrage pro Land, Art (Lager / Lieferzeit-Ware), Sprache und
 * Lieferort, egal wie viele Karten oder Varianten. Einteilung wie im Liefer-Widget
 * (altruan-liefer-timeline.liquid): Bestand > 0 oder Metafeld custom.always_available = Lager.
 *
 * Lieferort (Land, PLZ, Ort) und Endpoint kommen aus window.AltruanLieferort
 * (snippets/altruan-lieferort-head.liquid). Das dort gewählte Land ersetzt das Land des Aufrufers
 * (Shop-Markt), damit Karten, Preisblock und Warenkorb dem Land im Auswahlfenster folgen. Den Ort
 * einer eingetippten PLZ nennt die App (Feld city); er wird für den Knopf im Kopf gemerkt
 * (AltruanLieferort.remember). Ändert sich der Lieferort, werden die Anfragen verworfen; die Aufrufer
 * laden auf das Event "altruan-lieferort:change" neu.
 *
 * Die App antwortet maschinenlesbar:
 *   { "min_delivery_date": "2026-10-13", "max_delivery_date": "2026-10-14",
 *     "order_cutoff_at": "2026-10-12T13:00:00+02:00", "delivery_source": "measured",
 *     "label": "Lieferung Di, 13.10. – Mi, 14.10. · nach 84028 Landshut", "city": "Landshut" }
 * Angezeigt wird das fertige label in der Shop-Sprache. Zwischengespeichert wird in sessionStorage
 * höchstens 15 Minuten und nie über den Bestellschluss hinaus, weil danach ein anderer Liefertag gilt.
 *
 * Usage: window.AltruanLieferdatum.text({ country: "DE", inStock: true, locale: "de" }) → Promise<String|null>
 *        window.AltruanLieferdatum.info({ … }) → Promise<{ label, source, region, cutoffAt, minDate,
 *        maxDate, city }|null> (source "measured"/"fallback", region z. B. "94xxx" oder null – für das ⓘ
 *        im Preisblock; minDate/maxDate "2026-11-10" für das Bestelldatum im Warenkorb)
 */
(() => {
  if (window.AltruanLieferdatum) return;

  const DEFAULT_ENDPOINT = "https://shipping-app.altruan.de/public-shipping-date";
  const MAX_CACHE_MS = 15 * 60 * 1000;
  // Liegt der Bestellschluss laut Geräteuhr schon zurück (Uhr geht vor), nur kurz merken
  const PAST_CUTOFF_CACHE_MS = 60 * 1000;
  const CACHE_VERSION = "v6";
  const requests = {};

  const lieferort = () => window.AltruanLieferort || null;
  const endpoint = () => (lieferort() && lieferort().endpoint) || DEFAULT_ENDPOINT;
  const place = () => (lieferort() && lieferort().get()) || null;
  // Ort zur eingetippten PLZ für den Knopf im Kopf merken
  const rememberCity = (where, info) => {
    if (info && info.city && where && !where.city && !where.foundCity && lieferort() && lieferort().remember)
      lieferort().remember(where, info.city);
  };

  const load = (market, inStock, locale) => {
    const where = place();
    const country = (where && where.country) || market;
    const zip = where ? where.postal_code : "";
    const city = (where && where.city) || "";
    const key = `altruan-lieferdatum:${CACHE_VERSION}:${country}:${inStock ? "stock" : "backorder"}:${locale}:${zip}:${city}`;
    if (requests[key]) return requests[key];
    try {
      const hit = JSON.parse(sessionStorage.getItem(key) || "null");
      if (hit && Date.now() < hit.until && hit.info && typeof hit.info.label === "string") {
        rememberCity(where, hit.info);
        return (requests[key] = Promise.resolve(hit.info));
      }
    } catch (_) {}

    const body = {
      country,
      locale,
      variant_id: 1,
      total_inventory: inStock ? 1 : 0,
      variant_always_available: false,
    };
    if (zip) body.postal_code = zip;
    if (zip && city) body.city = city;

    requests[key] = fetch(endpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data || typeof data.label !== "string" || !data.label) return null;
        const info = {
          label: data.label,
          source: data.delivery_source === "measured" ? "measured" : "fallback",
          region: typeof data.postal_region === "string" ? data.postal_region : null,
          cutoffAt: typeof data.order_cutoff_at === "string" ? data.order_cutoff_at : null,
          minDate: typeof data.min_delivery_date === "string" ? data.min_delivery_date : null,
          maxDate: typeof data.max_delivery_date === "string" ? data.max_delivery_date : null,
          city: typeof data.city === "string" ? data.city : null,
        };
        rememberCity(where, info);
        const untilCutoff = Date.parse(data.order_cutoff_at) - Date.now();
        const ttl = untilCutoff > 0 ? Math.min(MAX_CACHE_MS, untilCutoff) : PAST_CUTOFF_CACHE_MS;
        try {
          sessionStorage.setItem(key, JSON.stringify({ until: Date.now() + ttl, info }));
        } catch (_) {}
        // Im Speicher nur bis Bestellschluss halten, danach neu fragen
        setTimeout(() => delete requests[key], ttl);
        return info;
      })
      .catch(() => {
        delete requests[key];
        return null;
      });
    return requests[key];
  };

  // Neuer Lieferort: alte Anfragen nicht mehr verwenden (sessionStorage ist je Lieferort getrennt)
  document.addEventListener("altruan-lieferort:change", () => {
    Object.keys(requests).forEach((key) => delete requests[key]);
  });

  const info = ({ country, inStock, locale }) => {
    const lang = String(locale || document.documentElement.lang || "de");
    return load(String(country || "DE").toUpperCase(), !!inStock, lang);
  };

  window.AltruanLieferdatum = {
    info,
    text(options) {
      return info(options).then((result) => (result ? result.label : null));
    },
  };
})();
